import type { PostingsV3DataQuery, PostingsV3Ent } from '@revelio/data-access';

import type { MapPoint } from '../../Chart/Map/types';
import { determineLocationType } from '../../Chart/Map/utils/location-type-utils';
import type { PostingMetricValues } from '../../PageConfigProvider/types/metric';
import type { LocationData } from './coordinate-resolver';

type PostingsV3MapField =
  | 'salary'
  | 'time_to_fill'
  | 'active'
  | 'added'
  | 'removed';

const mapMetricToField = (metric: PostingMetricValues): PostingsV3MapField => {
  switch (metric) {
    case 'posting_salary':
      return 'salary';
    case 'time_to_fill':
    case 'time_to_fill_percentile':
      return 'time_to_fill';
    case 'postings_new':
      return 'added';
    case 'postings_removed':
      return 'removed';
    default:
      return 'active';
  }
};

const getLatestValue = (
  points: ReadonlyArray<{ id: number; val: number }>
): number | null => {
  if (points.length === 0) return null;
  const latest = points.reduce((acc, point) =>
    point.id > acc.id ? point : acc
  );
  return latest.val;
};

export const convertPostingsV3MapData = ({
  data,
  requestEntities,
  metric,
  exchangeRate = 1,
  coords,
}: {
  data: PostingsV3DataQuery | undefined;
  requestEntities: ReadonlyArray<PostingsV3Ent>;
  metric: PostingMetricValues;
  exchangeRate?: number;
  coords: ReadonlyArray<LocationData>;
}): MapPoint[] => {
  const entities = data?.postings_v3?.entities ?? [];
  if (entities.length === 0) return [];

  const field = mapMetricToField(metric);
  const isSalary = metric === 'posting_salary';
  const coordMap = new Map(coords.map((c) => [c.id, c] as const));

  return entities
    .map((entity): MapPoint | null => {
      const requestEntity = requestEntities[entity.index];
      if (!requestEntity) return null;

      const coord = coordMap.get(requestEntity.EntID);
      if (!coord) return null;

      const rawValue = getLatestValue(entity.metrics[field]);
      if (rawValue == null) return null;

      const value = isSalary ? rawValue * exchangeRate : rawValue;
      const label = entity.short_name || entity.long_name || coord.name;

      return {
        id: String(requestEntity.EntID),
        label,
        lat: coord.lat,
        lon: coord.lon,
        value,
        locationType: determineLocationType(coord.name, coord.granularity),
      };
    })
    .filter((point): point is MapPoint => point !== null);
};
